import React, { useState, useEffect } from 'react';
import { COLORS } from '../utils/constants';
import { useNavigate, Link } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Mail, Lock, AlertCircle, Eye, EyeOff, CheckCircle, ChevronLeft, Check } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const passwordChecks = {
    length: password.length >= 6,
    hasNumber: /\d/.test(password),
    hasLetter: /[a-zA-Z]/.test(password),
  };

  const isPasswordValid = passwordChecks.length && passwordChecks.hasNumber && passwordChecks.hasLetter;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isPasswordValid) {
      setError(t('passwordNotMeetRequirements'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('passwordsNotMatch'));
      return;
    }

    if (!agreedToTerms) {
      setError(t('pleaseAgreeToTerms'));
      return;
    }

    setLoading(true);
    const result = await register(email, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleSocialLogin = (provider) => {
    alert(t('registrationComingSoon').replace('{provider}', provider));
  };

  // ==================== MOBILE LAYOUT ====================
  if (isMobile) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#0b0e11',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px',
          paddingTop: 'max(16px, env(safe-area-inset-top))',
          display: 'flex',
          alignItems: 'center',
        }}>
          <button
            onClick={() => window.history.back()}
            style={{
              background: 'none',
              border: 'none',
              color: '#848e9c',
              cursor: 'pointer',
              padding: '8px',
              marginLeft: '-8px',
            }}
          >
            <ChevronLeft size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '0 24px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {/* Logo */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
              <span style={{ color: COLORS.primary }}>Ex</span>
              <span style={{ color: '#eaecef' }}>Dex</span>
            </div>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#eaecef',
              margin: '0 0 8px 0',
            }}>
              {t('createAccount')}
            </h1>
            <p style={{ color: '#848e9c', fontSize: '14px', margin: 0 }}>
              {t('startJourney')}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              backgroundColor: '#f6465d15',
              border: '1px solid #f6465d40',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <AlertCircle size={20} color="#f6465d" />
              <span style={{ color: '#f6465d', fontSize: '14px' }}>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ flex: 1 }}>
            {/* Email */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#eaecef',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
              }}>
                {t('email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={t('enterEmail')}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: '#1e2329',
                  border: '1px solid #2b3139',
                  borderRadius: '12px',
                  color: '#eaecef',
                  fontSize: '16px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => e.target.style.borderColor = COLORS.primary}
                onBlur={(e) => e.target.style.borderColor = '#2b3139'}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#eaecef',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
              }}>
                {t('password')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder={t('enterPassword')}
                  style={{
                    width: '100%',
                    padding: '16px',
                    paddingRight: '50px',
                    backgroundColor: '#1e2329',
                    border: '1px solid #2b3139',
                    borderRadius: '12px',
                    color: '#eaecef',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.target.style.borderColor = '#2b3139'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                >
                  {showPassword ? <EyeOff size={20} color="#848e9c" /> : <Eye size={20} color="#848e9c" />}
                </button>
              </div>

              {/* Password Requirements */}
              {password && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[
                    { check: passwordChecks.length, label: t('minChars') },
                    { check: passwordChecks.hasLetter, label: t('hasLetter') },
                    { check: passwordChecks.hasNumber, label: t('hasNumber') },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: item.check ? '#0ecb8120' : '#2b3139',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {item.check && <Check size={10} color="#0ecb81" strokeWidth={3} />}
                      </div>
                      <span style={{ fontSize: '12px', color: item.check ? '#0ecb81' : '#848e9c' }}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#eaecef',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
              }}>
                {t('confirmYourPassword')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder={t('confirmPassword')}
                  style={{
                    width: '100%',
                    padding: '16px',
                    paddingRight: '50px',
                    backgroundColor: '#1e2329',
                    border: `1px solid ${confirmPassword && confirmPassword !== password ? '#f6465d' : '#2b3139'}`,
                    borderRadius: '12px',
                    color: '#eaecef',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.target.style.borderColor = confirmPassword && confirmPassword !== password ? '#f6465d' : '#2b3139'}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                >
                  {showConfirmPassword ? <EyeOff size={20} color="#848e9c" /> : <Eye size={20} color="#848e9c" />}
                </button>
              </div>
              {confirmPassword && confirmPassword !== password && (
                <div style={{ marginTop: '6px', color: '#f6465d', fontSize: '12px' }}>
                  {t('passwordsNotMatch')}
                </div>
              )}
            </div>

            {/* Terms Checkbox */}
            <div 
              style={{ 
                marginBottom: '20px', 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '12px',
                cursor: 'pointer',
              }}
              onClick={() => setAgreedToTerms(!agreedToTerms)}
            >
              <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '6px',
                border: `2px solid ${agreedToTerms ? COLORS.primary : '#2b3139'}`,
                backgroundColor: agreedToTerms ? COLORS.primary : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: '1px',
              }}>
                {agreedToTerms && <Check size={14} color="#000" strokeWidth={3} />}
              </div>
              <span style={{ color: '#848e9c', fontSize: '13px', lineHeight: '1.5' }}>
                {t('agreeToTerms')}{' '}
                <span style={{ color: COLORS.primary }}>{t('termsOfService')}</span>
                {' '}{t('and')}{' '}
                <span style={{ color: COLORS.primary }}>{t('privacyPolicy')}</span>
              </span>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !isPasswordValid || !agreedToTerms}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: COLORS.primary,
                color: '#000',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: (loading || !isPasswordValid || !agreedToTerms) ? 'not-allowed' : 'pointer',
                opacity: (loading || !isPasswordValid || !agreedToTerms) ? 0.5 : 1,
                marginBottom: '16px',
              }}
            >
              {loading ? t('loading') : t('createAccount')}
            </button>
          </form>

          {/* Social Auth */}
          <div style={{ marginTop: '24px', marginBottom: '20px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              marginBottom: '16px',
            }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#2b3139' }} />
              <span style={{ color: '#848e9c', fontSize: '13px' }}>{t('orContinueWith')}</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#2b3139' }} />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => handleSocialLogin('Google')} style={{ flex: 1, padding: '14px', backgroundColor: '#1e2329', border: '1px solid #2b3139', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="24" height="24" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              </button>
              <button onClick={() => handleSocialLogin('Telegram')} style={{ flex: 1, padding: '14px', backgroundColor: '#1e2329', border: '1px solid #2b3139', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#0088cc"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>
              </button>
            </div>
          </div>

          {/* Login Link */}
          <div style={{ 
            textAlign: 'center', 
            paddingBottom: 'max(80px, env(safe-area-inset-bottom))',
          }}>
            <span style={{ color: '#848e9c', fontSize: '14px' }}>
              {t('haveAccount')}{' '}
            </span>
            <Link
              to="/login"
              style={{
                color: COLORS.primary,
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              {t('signIn')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            <span style={{ color: COLORS.primary }}>Ex</span>
            <span style={{ color: COLORS.text }}>Dex</span>
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Decentralized Cryptocurrency Exchange
          </div>
        </div>

        {/* Register Form */}
        <div
          style={{
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '32px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.text, marginBottom: '8px' }}>
            {t('createAccount')}
          </h2>
          <p style={{ color: COLORS.textSecondary, fontSize: '14px', marginBottom: '32px' }}>
            {t('startJourney')}
          </p>

          {error && (
            <div style={{ backgroundColor: `${COLORS.danger}20`, border: `1px solid ${COLORS.danger}`, borderRadius: '8px', padding: '12px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <AlertCircle size={20} color={COLORS.danger} />
              <span style={{ color: COLORS.danger, fontSize: '14px' }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: COLORS.text, fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>{t('emailAddress')}</label>
              <div style={{ position: 'relative' }}>
                <Mail size={20} color={COLORS.textSecondary} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder={t('enterEmail')} style={{ width: '100%', padding: '12px 12px 12px 44px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} onFocus={(e) => (e.target.style.borderColor = COLORS.primary)} onBlur={(e) => (e.target.style.borderColor = COLORS.border)} />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: COLORS.text, fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>{t('password')}</label>
              <div style={{ position: 'relative' }}>
                <Lock size={20} color={COLORS.textSecondary} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder={t('enterPassword')} style={{ width: '100%', padding: '12px 44px 12px 44px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} onFocus={(e) => (e.target.style.borderColor = COLORS.primary)} onBlur={(e) => (e.target.style.borderColor = COLORS.border)} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                  {showPassword ? <EyeOff size={20} color={COLORS.textSecondary} /> : <Eye size={20} color={COLORS.textSecondary} />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: COLORS.text, fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>{t('confirmYourPassword')}</label>
              <div style={{ position: 'relative' }}>
                <Lock size={20} color={COLORS.textSecondary} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required placeholder={t('confirmPassword')} style={{ width: '100%', padding: '12px 44px 12px 44px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`, borderRadius: '8px', color: COLORS.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} onFocus={(e) => (e.target.style.borderColor = COLORS.primary)} onBlur={(e) => (e.target.style.borderColor = COLORS.border)} />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                  {showConfirmPassword ? <EyeOff size={20} color={COLORS.textSecondary} /> : <Eye size={20} color={COLORS.textSecondary} />}
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'start', gap: '12px' }}>
              <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} style={{ width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', accentColor: COLORS.primary }} />
              <label style={{ color: COLORS.textSecondary, fontSize: '13px', lineHeight: '1.5' }}>
                {t('agreeToTerms')} <a href="#" style={{ color: COLORS.primary, textDecoration: 'none' }}>{t('termsOfService')}</a> {t('and')} <a href="#" style={{ color: COLORS.primary, textDecoration: 'none' }}>{t('privacyPolicy')}</a>
              </label>
            </div>

            <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', backgroundColor: COLORS.primary, color: '#000', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? t('loading') : t('createAccount')}
            </button>
          </form>

          {/* Social Auth - Desktop */}
          <div style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: COLORS.border }} />
              <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>{t('orContinueWith')}</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: COLORS.border }} />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => handleSocialLogin('Google')} style={{ width: '64px', height: '64px', borderRadius: '50%', border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.background, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              </button>
              <button onClick={() => handleSocialLogin('Telegram')} style={{ width: '64px', height: '64px', borderRadius: '50%', border: `1px solid ${COLORS.border}`, backgroundColor: '#0088cc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="#FFF"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>
              </button>
            </div>
          </div>

          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>{t('haveAccount')} </span>
            <Link to="/login" style={{ color: COLORS.primary, textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>{t('signIn')}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
